import { ReactNode, useState } from 'react'
import {
	ComboBox,
	Label,
	Input,
	ListBox,
	ListBoxItem,
	Popover,
	Key,
	Text,
	ListBoxItemRenderProps,
} from 'react-aria-components'
import {
	Address,
	Brand,
	Client,
	Document,
	Expense,
	GenericObject,
	Location,
	ObjectProperties,
	Payment,
	Proposal,
	Reservation,
	User,
	Vehicle,
} from '../../shared/types'
import { ObjectTypes, useObjectCreate, useObjectList } from '../api/object'
import { useFormLayout } from '../layouts'
import { FormLayout } from '../layouts/form-layout'
import { useToggle } from '../use-toggle'
import { Button } from './button'
import { CurrencyFormatter } from './currency-formatter'
import { Dialog } from './dialog'
import { getUserLabel } from './user-formatter'
import { getVehicleLabel } from './vehicle-formatter'

type Reference<O extends keyof ObjectTypes> = {
	object: O
	label?: ReactNode
	renderOption: (item: ObjectTypes[O]) => ReactNode
	getTextValue: (item: ObjectTypes[O]) => string
	canCreate?: boolean
	filter?: ObjectProperties
}

type FieldState = { selectedKey: Key | null; inputValue: string }

type Props<O extends keyof ObjectTypes> = {
	object: O
	identifier?: string
	name?: string
	selectedKey?: Key | null
	onSelectionChange?: (id: Key | null) => void
	isRequired?: boolean
	autoFocus?: boolean
	additionalAction?: {
		name: string
		action: (value: string, setCreatedRelation: (object: ObjectTypes[O]) => void) => void
	}
}

const getClassName = ({
	isFocusVisible,
	isHovered,
	isPressed,
	isSelected,
}: ListBoxItemRenderProps) => {
	const className = ['cursor-pointer px-2 py-1 outline-0 rounded group']
	if (isPressed) {
		className.push('bg-blue-300')
	} else if (isSelected && (isHovered || isFocusVisible)) {
		className.push('bg-blue-100')
	} else if (isHovered || isFocusVisible) {
		className.push('bg-blue-50')
	} else if (isSelected) {
		className.push('bg-blue-200')
	}
	return className.join(' ')
}

export function ChildObject<O extends keyof ObjectTypes>({
	name,
	identifier,
	object,
	selectedKey = null,
	onSelectionChange,
	isRequired,
	additionalAction,
	autoFocus,
	...props
}: Props<O>) {
	const { renderOption, getTextValue, canCreate, filter } = REFERENCES[object]
	const [isCreating, toggleIsCreating] = useToggle()
	const [fieldState, setFieldState] = useState<FieldState>({
		selectedKey,
		inputValue: '',
	})
	const [createdRelation, setCreatedRelation] = useState<ObjectTypes[O] | undefined>(undefined)

	const { data: { list = [] } = {}, isPending } = useObjectList(object, {
		...filter,
		fulltext: fieldState.inputValue,
		page: 1,
		orderKey: 'name',
		orderDir: 'ASC',
	})

	const handleSelectionChange = (id: Key | null) => {
		const item = items.find(o => o.id === id)
		setFieldState({
			inputValue: item ? getTextValue(item) : String(id ?? ''),
			selectedKey: id,
		})
		onSelectionChange?.(id)
	}

	const handleInputChange = (value: string) => {
		setFieldState(prevState => ({
			inputValue: value ?? '',
			selectedKey: value === '' ? null : prevState.selectedKey,
		}))
	}

	const items: Array<ObjectTypes[O]> = [...list]
	if (createdRelation && !items.find(item => item.id === createdRelation.id)) {
		items.push(createdRelation)
	}
	let inputValue = fieldState.inputValue
	if (!inputValue && fieldState.selectedKey) {
		const selectedItem = items.find(item => item.id === fieldState.selectedKey)
		if (selectedItem) {
			inputValue = getTextValue(selectedItem)
		}
	}

	return (
		<>
			<input type="hidden" name={identifier} value={fieldState.selectedKey ?? ''} />
			<ComboBox
				{...props}
				items={items}
				defaultFilter={() => true}
				selectedKey={fieldState.selectedKey}
				inputValue={inputValue}
				onSelectionChange={handleSelectionChange}
				onInputChange={handleInputChange}
				allowsEmptyCollection
				isRequired={isRequired}
				className={
					!fieldState.selectedKey
						? `[&_button]:invalid:border-red-600 [&_input]:invalid:border-red-600`
						: undefined
				}
				autoFocus={autoFocus}
			>
				{name ? (
					<Label className="mb-1 flex font-normal leading-tight text-xs">{name}</Label>
				) : null}
				<div className="flex">
					<div className="flex flex-auto bg-transparent text-sm text-gray-700 font-sans font-normal outline outline-0">
						<Input className="flex-auto w-full p-2 bg-transparent outline outline-0 disabled:border-gray-100 border border-gray-400 rounded-l focus:border-gray-900" />
					</div>
					<Button variant="INVISIBLE" className="rounded-l-none">
						▼
					</Button>
				</div>
				<Popover className="bg-white rounded border border-gray-200 shadow-xl shadow-gray-200 overflow-auto">
					{items.length === 0 && !isPending ? (
						<div className="text-center p-4 space-y-4">
							<p>Nebyly nalezeny žádné výsledky.</p>
							<div className="flex flex-wrap gap-4 justify-center">
								{canCreate ? (
									<Button
										onPressStart={toggleIsCreating}
										variant="PRIMARY"
										preventFocusOnPress
									>
										＋ Vytvořit
									</Button>
								) : null}
								{additionalAction ? (
									<Button
										type="button"
										onPressStart={() => {
											additionalAction.action(
												fieldState.inputValue,
												child => {
													setCreatedRelation(child)
													setFieldState({
														inputValue: getTextValue(child),
														selectedKey: child.id,
													})
												},
											)
										}}
										variant="SECONDARY"
										preventFocusOnPress
									>
										{additionalAction.name}
									</Button>
								) : null}
							</div>
						</div>
					) : (
						<ListBox className={`flex flex-col gap-[1px] ${items.length ? 'p-2' : ''}`}>
							{(item: ObjectTypes[O]) => (
								<ListBoxItem
									className={getClassName}
									textValue={getTextValue(item)}
								>
									{renderOption(item)}
								</ListBoxItem>
							)}
						</ListBox>
					)}
				</Popover>
			</ComboBox>
			{isCreating ? (
				<ChildObjectForm
					object={object}
					onClose={toggleIsCreating}
					onCreate={setCreatedRelation}
					fieldState={fieldState}
					setFieldState={setFieldState}
				/>
			) : null}
		</>
	)
}

type FormProps<O extends keyof ObjectTypes> = {
	object: O
	onClose: VoidFunction
	onCreate: (child: ObjectTypes[O]) => void
	fieldState: FieldState
	setFieldState: (fieldState: FieldState) => void
}

function ChildObjectForm<O extends keyof ObjectTypes>({
	onClose,
	onCreate,
	object,
	fieldState,
	setFieldState,
}: FormProps<O>) {
	const { getTextValue } = REFERENCES[object]
	const { layout } = useFormLayout(object)
	const { mutate, isPending: isCreatingPending } = useObjectCreate(object)

	return (
		<Dialog heading="Nový objekt" isOpen onOpenChange={onClose}>
			<form
				className="p-4 space-y-4 m-auto max-w-[60em] w-[480px]"
				onSubmit={e => {
					e.stopPropagation()
					e.preventDefault()
					mutate?.(new FormData(e.currentTarget), {
						onSuccess: child => {
							if (typeof child === 'object') {
								onCreate(child)
								setFieldState({
									inputValue: getTextValue(child),
									selectedKey: child.id,
								})
							}
							onClose()
						},
					})
				}}
			>
				<FormLayout layout={layout} data={{ name: fieldState.inputValue }} />
				<Button type="submit" isPending={isCreatingPending} isDisabled={isCreatingPending}>
					Uložit
				</Button>
			</form>
		</Dialog>
	)
}

export const REFERENCES = {
	address: {
		object: 'address',
		label: 'Adresa',
		canCreate: true,
		getTextValue: (item: Address) => item.id,
		renderOption: (item: Address) => <Text slot="label">{item.id}</Text>,
	} as Reference<'address'>,
	brand: {
		object: 'brand',
		label: 'Pronajímatel',
		canCreate: true,
		getTextValue: (item: Brand) => item.name,
		renderOption: (item: Brand) => {
			return <Text slot="label">{item.name}</Text>
		},
	} as Reference<'brand'>,
	client: {
		object: 'client',
		label: 'Zákazník',
		canCreate: true,
		getTextValue: (item: Client) => item.name,
		renderOption: (item: Client) => {
			const addresses = (item as any).addresses || []
			const phones = [...new Set(addresses.map((a: any) => a.phone).filter(Boolean))]
			const emails = [...new Set(addresses.map((a: any) => a.email).filter(Boolean))]
			const mainAddress = addresses[0]

			return (
				<div className="py-1">
					<div className="flex justify-between items-baseline">
						<Text slot="label" className="font-medium text-gray-900">
							{item.name}
						</Text>
						<span className="text-xs text-gray-400 ml-2">#{item.id}</span>
					</div>
					<Text slot="description" className="block text-xs text-gray-500 mb-1">
						IČO: {item.ico ?? '-'} {item.dic ? `, DIČ: ${item.dic}` : ''}
					</Text>

					{(phones.length > 0 || emails.length > 0) && (
						<div className="mt-1 pt-1 border-t border-gray-100 space-y-0.5">
							{phones.length > 0 && (
								<Text
									slot="description"
									className="block text-xs text-blue-700 font-medium"
								>
									📞 {phones.join(', ')}
								</Text>
							)}
							{emails.length > 0 && (
								<Text
									slot="description"
									className="block text-xs text-gray-600 truncate"
								>
									✉️ {emails.join(', ')}
								</Text>
							)}
						</div>
					)}

					{mainAddress ? (
						<div className="mt-1 text-[10px] text-gray-500 truncate">
							📍 {mainAddress.street ? `${mainAddress.street}, ` : ''}
							{mainAddress.city}
							{mainAddress.postal_code ? ` (${mainAddress.postal_code})` : ''}
						</div>
					) : (
						<Text
							slot="description"
							className="block text-xs text-gray-400 italic mt-1"
						>
							Bez adresy
						</Text>
					)}
				</div>
			)
		},
	} as Reference<'client'>,
	document: {
		object: 'document',
		label: 'Document',
		canCreate: true,
		getTextValue: (item: Document) => item.name,
		renderOption: (item: Document) => {
			return <Text slot="label">{item.name}</Text>
		},
	} as Reference<'document'>,
	expense: {
		object: 'expense',
		label: 'Výdaj',
		canCreate: true,
		getTextValue: (item: Expense) => item.id,
		renderOption: (item: Expense) => (
			<Text slot="label">
				<CurrencyFormatter value={item.value} />
			</Text>
		),
	} as Reference<'expense'>,
	image: {
		object: 'image',
		label: 'Obrázek',
		canCreate: true,
		getTextValue: (item: GenericObject) => item.id,
		renderOption: (item: GenericObject) => <Text slot="label">{item.id}</Text>,
	} as Reference<'image'>,
	location: {
		object: 'location',
		label: 'Pobočka',
		canCreate: true,
		getTextValue: (item: Location) => item.name,
		renderOption: (location: Location) => {
			return <Text slot="label">{location.name}</Text>
		},
	} as Reference<'location'>,
	payment: {
		object: 'payment',
		label: 'Přijatý příjem',
		canCreate: true,
		getTextValue: (item: Payment) => item.id,
		renderOption: (item: Payment) => (
			<Text slot="label">
				<CurrencyFormatter value={item.value} />
			</Text>
		),
	} as Reference<'payment'>,
	proposal: {
		object: 'proposal',
		label: 'Poptávka',
		canCreate: true,
		getTextValue: (item: Proposal) => item.id,
		renderOption: (item: Proposal) => <Text slot="label">{item.name ?? item.id}</Text>,
	} as Reference<'proposal'>,
	proposal_vehicle: {
		object: 'proposal_vehicle',
		label: 'Vozidlo poptávky',
		canCreate: true,
		getTextValue: (item: GenericObject) => item.name,
		renderOption: (item: GenericObject) => <Text slot="label">{item.name}</Text>,
	} as Reference<'proposal_vehicle'>,
	reservation: {
		object: 'reservation',
		label: 'Rezervace',
		canCreate: true,
		getTextValue: (item: Reservation) => item.id,
		renderOption: (reservation: Reservation) => {
			return <Text slot="label">{reservation.id}</Text>
		},
	} as Reference<'reservation'>,
	user: {
		object: 'user',
		label: 'Uživatel',
		canCreate: false,
		renderOption: (user: User) => {
			return <Text slot="label">{getUserLabel(user)}</Text>
		},
		getTextValue: getUserLabel,
	} as Reference<'user'>,
	vehicle: {
		object: 'vehicle',
		label: 'Vozidlo',
		canCreate: true,
		getTextValue: (item: Vehicle) => item.name,
		renderOption: (vehicle: Vehicle) => {
			return <Text slot="label">{getVehicleLabel(vehicle)}</Text>
		},
		filter: {
			active: true,
		},
	} as Reference<'vehicle'>,
} as const
